import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Search, Link2, Info, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { URLTable } from '../components/URLTable'
import { linkTrackerApi } from '../api'
import type { URLsPaginadas, FiltrosURL } from '../types'
import { CANALES } from '../types'

export function ListaURLs() {
  const [data, setData] = useState<URLsPaginadas | null>(null)
  const [loading, setLoading] = useState(true)
  const [grupoActivo, setGrupoActivo] = useState<string>('tonal')
  const [filtros, setFiltros] = useState<FiltrosURL>({
    page: 1,
    page_size: 20,
    grupo: 'tonal',
  })
  const [showInfo, setShowInfo] = useState(false)

  useEffect(() => {
    cargarURLs()
  }, [filtros])

  const cargarURLs = async () => {
    setLoading(true)
    try {
      const result = await linkTrackerApi.getURLs(filtros)
      setData(result)
    } catch (error) {
      console.error('Error:', error)
      toast.error('Error al cargar URLs')
    } finally {
      setLoading(false)
    }
  }

  const handleSearchChange = (value: string) => {
    setFiltros({ ...filtros, search: value || undefined, page: 1 })
  }

  const handleTabChange = (value: string) => {
    setGrupoActivo(value)
    setFiltros({
      ...filtros,
      grupo: value,
      page: 1,
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Link2 className="h-6 w-6" />
            URLs Acortadas
          </h1>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="text-muted-foreground hover:text-foreground transition-colors ml-2"
          >
            <Info className="h-5 w-5" />
          </button>
          {showInfo && (
            <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
              Gestiona tus URLs con tracking de clics
            </span>
          )}
        </div>
        <Button asChild className="bg-teal-800 hover:bg-teal-900 text-white">
          <Link to="/link-tracker/crear">
            <Plus className="h-4 w-4 mr-2" />
            Nueva URL
          </Link>
        </Button>
      </div>

      {/* Tabs por grupo */}
      <Tabs value={grupoActivo} onValueChange={handleTabChange}>
        <TabsList className="w-full">
          <TabsTrigger value="tonal" className="flex-1">Tonal</TabsTrigger>
          <TabsTrigger value="personal" className="flex-1">Personal</TabsTrigger>
        </TabsList>

        <TabsContent value={grupoActivo} className="space-y-6 mt-4">
          {/* Filtros */}
          <Card className="border border-gray-300 dark:border-gray-700">
            <CardContent className="pt-6">
              <div className="flex gap-4">
                <div className="w-1/2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar por código, título..."
                      className="pl-10"
                      value={filtros.search || ''}
                      onChange={(e) => handleSearchChange(e.target.value)}
                    />
                  </div>
                </div>
                <div className="w-1/4">
                  <Select
                    value={filtros.canal || 'all'}
                    onValueChange={(value) => setFiltros({
                      ...filtros,
                      canal: value === 'all' ? undefined : value,
                      page: 1
                    })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Canal" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los canales</SelectItem>
                      {CANALES.map((canal) => (
                        <SelectItem key={canal.value} value={canal.value}>
                          {canal.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-1/4">
                  <Select
                    value={filtros.activa === undefined ? 'all' : filtros.activa.toString()}
                    onValueChange={(value) => setFiltros({
                      ...filtros,
                      activa: value === 'all' ? undefined : value === 'true',
                      page: 1
                    })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los estados</SelectItem>
                      <SelectItem value="true">Activas</SelectItem>
                      <SelectItem value="false">Inactivas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabla */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                URLs
                {data && (
                  <Badge className="badge-gradient hover:opacity-90">
                    {data.count}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
                </div>
              ) : data && data.results.length > 0 ? (
                <>
                  <URLTable urls={data.results} onRefresh={cargarURLs} />

                  {/* Paginación */}
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Mostrar</span>
                      <Select
                        value={filtros.page_size.toString()}
                        onValueChange={(value) => setFiltros({ ...filtros, page: 1, page_size: parseInt(value) })}
                      >
                        <SelectTrigger className="w-[80px] h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="text-sm text-muted-foreground">por página</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setFiltros({ ...filtros, page: 1 })}
                        disabled={filtros.page === 1}
                      >
                        <ChevronsLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setFiltros({ ...filtros, page: filtros.page - 1 })}
                        disabled={filtros.page === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm text-muted-foreground min-w-[100px] text-center">
                        {filtros.page} de {data.total_pages}
                      </span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setFiltros({ ...filtros, page: filtros.page + 1 })}
                        disabled={filtros.page === data.total_pages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setFiltros({ ...filtros, page: data.total_pages })}
                        disabled={filtros.page === data.total_pages}
                      >
                        <ChevronsRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                  <p>No se encontraron URLs</p>
                  <Button asChild variant="link" className="mt-2">
                    <Link to="/link-tracker/crear">Crear primera URL</Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
